import React from "react";
import classNames from "classnames";
import { Link } from "react-router-dom";
import { SectionTilesProps } from "../../../utils/SectionProps";
import SectionHeader from "../partials/SectionHeader";
import {
  warHeader,
  warPillars,
  warProcess,
  warCta,
} from "../../../data/wellArchitected";

const propTypes = {
  ...SectionTilesProps.types,
};

const defaultProps = {
  ...SectionTilesProps.defaults,
};

const svgProps = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.6,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": true,
};

const PillarIcon = ({ name }) => {
  switch (name) {
    case "security":
      return (
        <svg {...svgProps}>
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          <path d="M9 12l2 2 4-4" />
        </svg>
      );
    case "reliability":
      return (
        <svg {...svgProps}>
          <path d="M23 4v6h-6" />
          <path d="M1 20v-6h6" />
          <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10" />
          <path d="M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
        </svg>
      );
    case "performance":
      return (
        <svg {...svgProps}>
          <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
        </svg>
      );
    case "cost":
      return (
        <svg {...svgProps}>
          <circle cx="12" cy="12" r="9" />
          <path d="M14.5 9a2.5 2.5 0 0 0-2.5-1.5c-1.5 0-2.5.8-2.5 2s1 1.7 2.5 2 2.5.8 2.5 2-1 2-2.5 2A2.5 2.5 0 0 1 9.5 16" />
          <path d="M12 6v1.5M12 16.5V18" />
        </svg>
      );
    case "sustainability":
      return (
        <svg {...svgProps}>
          <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10z" />
          <path d="M2 22c1.5-3 3-5 6-7" />
        </svg>
      );
    case "operations":
    default:
      return (
        <svg {...svgProps}>
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
        </svg>
      );
  }
};

const WellArchitected = ({
  className,
  topOuterDivider,
  bottomOuterDivider,
  topDivider,
  bottomDivider,
  hasBgColor,
  invertColor,
  ...props
}) => {
  const outerClasses = classNames(
    "features-tiles section well-architected",
    topOuterDivider && "has-top-divider",
    bottomOuterDivider && "has-bottom-divider",
    hasBgColor && "has-bg-color",
    invertColor && "invert-color",
    className
  );

  const innerClasses = classNames(
    "testimonial-inner section-inner",
    topDivider && "has-top-divider",
    bottomDivider && "has-bottom-divider"
  );

  return (
    <section {...props} className={outerClasses}>
      <div className="container">
        <div className={innerClasses}>
          <SectionHeader data={warHeader} className="center-content pb-0" />

          <div className="war-inner">
            <div className="war-pillars">
              {warPillars.map((pillar, i) => (
                <div
                  key={pillar.id}
                  className="war-pillar reveal-from-bottom"
                  data-reveal-delay={(i % 3) * 100}
                >
                  <span className="war-pillar-icon">
                    <PillarIcon name={pillar.icon} />
                  </span>
                  <h5 className="war-pillar-title">{pillar.title}</h5>
                  <p className="war-pillar-desc">{pillar.description}</p>
                </div>
              ))}
            </div>

            <p className="war-process-label">How the review works</p>
            <div className="war-process">
              {warProcess.map((stage, i) => (
                <div
                  key={stage.step}
                  className="war-step reveal-from-bottom"
                  data-reveal-delay={i * 100}
                >
                  <span className="war-step-num">{stage.step}</span>
                  <h6 className="war-step-title">{stage.title}</h6>
                  <p className="war-step-desc">{stage.description}</p>
                </div>
              ))}
            </div>

            <div className="war-cta reveal-from-bottom">
              <div className="war-cta-text">
                <div className="war-cta-heading">{warCta.heading}</div>
                <p className="war-cta-sub">{warCta.text}</p>
              </div>
              <Link to={warCta.buttonLink} className="war-cta-btn">
                {warCta.buttonLabel}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

WellArchitected.propTypes = propTypes;
WellArchitected.defaultProps = defaultProps;

export default WellArchitected;
